import React, { useState, useMemo, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import { Search, Upload, Download, Calendar, List, LayoutGrid, Plus, Phone, ChevronRight, Trash2, ChevronUp, ChevronDown, ChevronsLeft, ChevronLeft, ChevronsRight, Filter, AlertTriangle, ClipboardList, Briefcase, CheckCircle2, XCircle, Clock, Trophy, User } from 'lucide-react';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem, DatePicker } from '../ui';
import { safeAmount, formatCurrency, formatDate } from '../../utils/helpers';
import { STAGE_COLORS, PREQUALIFICATION_STATUSES, AWARD_STATUSES, APPROVAL_STATUS_CONFIG } from '../../lib/constants';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSocket } from '../../hooks/useSocket';
import * as XLSX from 'xlsx';
import CsvImportModal from '../modals/CsvImportModal';
import CsvSetupModal from '../modals/CsvSetupModal';
import PrequalificationsKanbanView from './PrequalificationsKanbanView';
import DeleteConfirmModal from '../modals/DeleteConfirmModal';

export default function PrequalificationsView({
  data: rawData,
  isLoading,
  onSelectLead,
  onShowNewLead,
  onFileUpload,
  onTruncateLeads,
  onStageChange,
  currentUser,
  newPrequalificationIds = [],
  viewedPrequalificationIds = [],
  importProgress,
  closeImportModal,
  fetchAllLeads,
  pagination,
  users = [],
  sources = [],
  totalLeads = 0,
}) {
  const { t } = useLanguage();
  const { socket } = useSocket();
  const isSales = currentUser?.role === 'Sales';

  const data = React.useMemo(() => {
    return (rawData || []).map(p => {
      const srcName = typeof p.source === 'object' && p.source !== null
        ? p.source.name
        : (typeof p.source === 'string' && p.source ? p.source : '');
      const fallbackSrc = (p.sourceId && sources?.find(s => s.id == p.sourceId)?.name)
        || (typeof p.profile?.source === 'object' ? p.profile?.source?.name : (typeof p.profile?.source === 'string' && p.profile.source !== 'Manual Entry' ? p.profile.source : ''))
        || '';
      return {
        ...p,
        source: srcName || fallbackSrc || '',
        sourceId: p.sourceId ?? (typeof p.source === 'object' ? p.source?.id : null)
      };
    });
  }, [rawData, sources]);


  useEffect(() => {
    if (!socket || !fetchAllLeads) return;
    const handleRealtimeNewLead = () => {
      fetchAllLeads();
    };
    socket.on('new-prequalification', handleRealtimeNewLead);
    socket.on('lead_created', handleRealtimeNewLead);
    socket.on('lead_updated', handleRealtimeNewLead);
    return () => {
      socket.off('new-prequalification', handleRealtimeNewLead);
      socket.off('lead_created', handleRealtimeNewLead);
      socket.off('lead_updated', handleRealtimeNewLead);
    };
  }, [socket, fetchAllLeads]);

  const [globalFilter, setGlobalFilter] = useState('');
  const [approvalFilter, setApprovalFilter] = useState('All');
  // Multi-select filters: an empty Set means "All"
  const [stageFilter, setStageFilter] = useState(new Set());
  const [assigneeFilter, setAssigneeFilter] = useState(new Set());
  const [sourceFilter, setSourceFilter] = useState(new Set());
    
  const filterStages = useMemo(() => {
    return PREQUALIFICATION_STATUSES;
  }, []);

  const activeStages = useMemo(() => {
    return PREQUALIFICATION_STATUSES;
  }, []);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Which date the from/to range filters on.
  const [dateFilterType, setDateFilterType] = useState('creationDate');
  // Newest inquiry first.
  const [sorting, setSorting] = useState([{ id: 'dateColumn', desc: true }]);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('leadsViewMode') || 'list');
  const [showImportSetup, setShowImportSetup] = useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);

  const [{ pageIndex, pageSize }, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: parseInt(localStorage.getItem('leadsPageSize')) || 10,
  });

  React.useEffect(() => {
    localStorage.setItem('leadsPageSize', pageSize);
  }, [pageSize]);

  const handleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('leadsViewMode', mode);
  };

  // Dates arrive in two shapes — dd-mm-yyyy from the banquet system and
  // ISO from the CRM — so both are normalised to yyyy-mm-dd before comparing.
  const dateSortingFn = (rowA, rowB, columnId) => {
    const norm = (v) => {
      const s = String(v || '');
      if (!s) return '';
      const p = s.split('-');
      return (p.length === 3 && p[2].length === 4)
        ? `${p[2]}-${p[1]}-${p[0].padStart(2, '0')}`
        : s.split('T')[0];
    };
    const a = norm(rowA.getValue(columnId));
    const b = norm(rowB.getValue(columnId));
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    return a === b ? 0 : (a > b ? 1 : -1);
  };

  // Client-side filtering of the global prequalifications array
  const roleFilteredData = useMemo(() => {
    let filtered = data ? [...data] : [];
    

    let leadsToFilter = data || [];

    return leadsToFilter.filter(prequalification => {
      // Do not filter out Enlisted prequalifications here, they should be visible in the Enlisted stage
      if (prequalification.leadType === 'PROPOSAL_TENDER') return false;

      // Role check (if not admin/owner/lead, they only see their own assigned prequalifications)
      if (!['CEO', 'GM_Operations', 'BM_Operations', 'Senior_Team_Lead', 'Team_Lead'].includes(currentUser?.role)) {
        if (prequalification.manager !== currentUser?.username && prequalification.assignedTo !== currentUser?.id) {
          return false;
        }
      }

      let filtered = true;
      if (globalFilter) {
        const search = globalFilter.toLowerCase();
        filtered = (prequalification.clientName && prequalification.clientName.toLowerCase().includes(search)) || 
          (prequalification.contactPerson && prequalification.contactPerson.toLowerCase().includes(search)) ||
          (prequalification.phone && prequalification.phone.toLowerCase().includes(search)) ||
          (prequalification.title && prequalification.title.toLowerCase().includes(search)) ||
          (prequalification.approvalStatus && prequalification.approvalStatus.toLowerCase().includes(search));
      }

      if (!filtered) return false;

      if (approvalFilter !== 'All') {
        const itemApprovalStatus = prequalification.approvalStatus || 'APPROVED';
        if (itemApprovalStatus !== approvalFilter) return false;
      }

      if (stageFilter.size) {
        if (stageFilter.has('New') && (prequalification.stage === 'New' || prequalification.stage === 'New Prequalification')) return true;
        if (!stageFilter.has(prequalification.stage)) return false;
      }

      const assigneeName = prequalification.assignee?.username || 'Unassigned';
      if (assigneeFilter.size && !assigneeFilter.has(assigneeName)) return false;

      if (sourceFilter.size && !sourceFilter.has(prequalification.source)) return false;
      
      if (startDate) {
        let dateVal = '';
        if (dateFilterType === 'enlistedDate') dateVal = prequalification.enlistedDate || '';
        else if (dateFilterType === 'creationDate') dateVal = prequalification.inquiryDate || prequalification.createdAt || '';
        const datePart = dateVal ? String(dateVal).split('T')[0] : '';
        if (!datePart) return false;
        
        let normalizedDate = datePart;
        const parts = datePart.split('-');
        if (parts.length === 3 && parts[2].length === 4) {
          normalizedDate = `${parts[2]}-${parts[1]}-${parts[0].padStart(2, '0')}`;
        }
        if (normalizedDate < startDate) return false;
      }
      
      if (endDate) {
        let dateVal = '';
        if (dateFilterType === 'enlistedDate') dateVal = prequalification.enlistedDate || '';
        else if (dateFilterType === 'creationDate') dateVal = prequalification.inquiryDate || prequalification.createdAt || '';
        const datePart = dateVal ? String(dateVal).split('T')[0] : '';
        if (!datePart) return false;

        let normalizedDate = datePart;
        const parts = datePart.split('-');
        if (parts.length === 3 && parts[2].length === 4) {
          normalizedDate = `${parts[2]}-${parts[1]}-${parts[0].padStart(2, '0')}`;
        }
        if (normalizedDate > endDate) return false;
      }
      
      return true;
    });
  }, [data, globalFilter, approvalFilter, stageFilter, assigneeFilter, sourceFilter, startDate, endDate, dateFilterType, currentUser]);


  // Define columns tailored to Prequalification & Proposal workflow
  const columns = useMemo(() => [
    {
      id: 'clientName',
      accessorKey: 'clientName',
      header: t('client') || 'Client Profile',
      cell: ({ row }) => {
        const prequalification = row.original;
        const name = prequalification.clientName;
        const displayName = name && name.includes('null') ? name.replace(/\s*null\s*/, '').trim() : (name || 'Client #' + prequalification.id);
        const contactPerson = prequalification.contactPerson || prequalification.contact?.contactPersonName || prequalification.profile?.contactPersonName;
        return (
          <div className="flex flex-col">
            <button
              onClick={() => onSelectLead(prequalification)}
              className="font-bold text-blue-600 hover:text-blue-800 hover:underline text-left text-sm"
            >
              {prequalification.projectName || displayName}
            </button>
            {prequalification.projectName ? (
              <span className="text-[11px] text-gray-500 font-medium flex items-center gap-1 mt-0.5">
                <User className="w-3 h-3" /> {displayName}
              </span>
            ) : contactPerson && (
              <span className="text-[11px] text-gray-500 font-medium flex items-center gap-1 mt-0.5">
                <User className="w-3 h-3" /> {contactPerson}
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'approvalStatus',
      header: t('approval') || 'Approval',
      cell: ({ row }) => {
        const val = row.original.approvalStatus || 'APPROVED';
        const config = APPROVAL_STATUS_CONFIG[val] || APPROVAL_STATUS_CONFIG['APPROVED'];
        const isPending = val === 'PENDING_APPROVAL';
        const isRejected = val === 'REJECTED';
        const assignedUsername = row.original.assignee?.username || (typeof row.original.assignedTo === 'object' ? row.original.assignedTo?.username : null);
        const creatorUsername = row.original.createdBy?.username || row.original.creator?.username || (typeof row.original.createdBy === 'string' ? row.original.createdBy : null);

        return (
          <div className="flex flex-col items-start gap-1">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap w-fit ${config.badge}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                val === 'APPROVED' ? 'bg-emerald-500' :
                isPending ? 'bg-amber-500 animate-pulse' :
                'bg-rose-500'
              }`} />
              {t(config.translationKey) || config.label}
            </span>
            {assignedUsername && (
              <span className="text-[11px] text-gray-500 truncate flex items-center gap-1" title={`Assigned to: ${assignedUsername}`}>
                <User className="w-3 h-3 text-gray-400 shrink-0" />
                <span className="truncate">{assignedUsername}</span>
              </span>
            )}
            {!assignedUsername && creatorUsername && (
              <span className="text-[11px] text-gray-400 truncate flex items-center gap-1" title={`Created by: ${creatorUsername}`}>
                <User className="w-3 h-3 text-gray-400 shrink-0" />
                <span className="truncate">{creatorUsername}</span>
              </span>
            )}
            {isRejected && row.original.approvalRemarks && (
              <span className="text-[10px] text-rose-600 truncate max-w-[140px] flex items-center gap-1" title={`Reason: ${row.original.approvalRemarks}`}>
                <AlertTriangle className="w-3 h-3 text-rose-500 shrink-0" />
                <span className="truncate">{row.original.approvalRemarks}</span>
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'stage',
      header: t('stage'),
      cell: ({ getValue }) => {
        const stage = getValue();
        const color = STAGE_COLORS[stage] || STAGE_COLORS['New'];
        return (
          <span className={`px-2 py-1 rounded text-xs font-bold inline-block whitespace-nowrap ${color.bg} ${color.text}`}>
            {stage}
          </span>
        );
      },
    },
    {
      id: 'services',
      header: 'Services',
      accessorFn: (row) => {
        if (!row.services || !row.services.length) return '';
        return row.services.map(s => typeof s === 'string' ? s : s.name).join(', ');
      },
      cell: ({ getValue }) => {
        const servicesStr = getValue();
        if (!servicesStr) return <span className="text-gray-300">-</span>;
        const list = servicesStr.split(', ');
        return (
          <div className="flex flex-wrap gap-1 max-w-[200px]">
            {list.map((s, idx) => (
              <span key={idx} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200 truncate" title={s}>
                {s}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: 'documentStatus',
      header: t('docStatus') || 'Doc Status',
      cell: ({ row }) => {
        const docStatus = row.original.documentStatus;
        if (!docStatus) return <span className="text-gray-400 text-xs">-</span>;
        
        let colorClass = 'bg-gray-100 text-gray-700';
        if (docStatus.includes('Received') || docStatus === 'Complete') colorClass = 'bg-green-100 text-green-700 font-bold';
        if (docStatus.includes('To be') || docStatus === 'Pending') colorClass = 'bg-amber-100 text-amber-700';
        if (docStatus.includes('Missing')) colorClass = 'bg-red-100 text-red-700 font-bold';

        return (
          <span className={`px-2 py-1 rounded text-xs inline-block whitespace-nowrap ${colorClass}`}>
            {docStatus}
          </span>
        );
      }
    },
    {
      id: 'earnestMoney',
      header: 'Earnest Money',
      cell: ({ row }) => {
        const { earnestMoneyRequired, earnestMoneyValue } = row.original;
        if (!earnestMoneyRequired) return <span className="text-gray-400 text-xs text-center block w-full">-</span>;
        
        return (
          <span className="text-xs font-medium text-gray-700 whitespace-nowrap bg-blue-50 px-2 py-1 rounded border border-blue-100">
            {earnestMoneyValue ? Number(earnestMoneyValue).toLocaleString() : '0'}
          </span>
        );
      }
    },

    {
      accessorKey: 'source',
      header: t('source'),
      cell: ({ getValue }) => {
        const source = getValue();
        return source ? (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 inline-block whitespace-nowrap">
            {source}
          </span>
        ) : <span className="text-gray-400">-</span>;
      },
    },
    {
      id: 'assignee',
      accessorFn: (row) => row.assignee?.username || 'Unassigned',
      header: t('assignee') || 'Assignee',
      cell: ({ getValue }) => {
        const a = getValue();
        return a && a !== 'Unassigned' ? (
          <span className="text-gray-700 text-xs font-medium">{a}</span>
        ) : (
          <span className="text-amber-600 font-semibold text-xs">{t('unassigned') || 'Unassigned'}</span>
        );
      },
    },
    {
      id: 'dateColumn',
      accessorFn: (row) => {
        if (dateFilterType === 'enlistedDate') return row.enlistedDate;
        return row.submissionDate || row.inquiryDate || row.createdAt || undefined;
      },
      sortUndefined: 'last',
      header: dateFilterType === 'enlistedDate' ? (t('enlistedDate') || 'Enlisted Date') : (t('creationDate') || 'Creation Date'),
      cell: ({ getValue }) => {
        const val = getValue();
        return val ? <span className="text-gray-500 text-xs">{formatDate(val)}</span> : <span className="text-gray-300">-</span>;
      },
      sortingFn: dateSortingFn,
    },
  ], [onSelectLead, t, dateFilterType]);

  // Initialize table
  const table = useReactTable({
    data: roleFilteredData,
    columns,
    state: {
      sorting,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    onPaginationChange: setPaginationState,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const uniqueAssignees = useMemo(() => {
    const assignees = (data || []).map(p => p.assignee?.username).filter(Boolean);
    return [...new Set(assignees)].sort();
  }, [data]);
  const uniqueSources = useMemo(
    () => {
      if (sources && sources.length > 0) return [...sources].map(s => s.name).sort((a, b) => a.localeCompare(b));
      return [...new Set((data || []).map(l => l.source).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    },
    [data, sources]
  );
  const getStageColor = (stage) => STAGE_COLORS[stage] || STAGE_COLORS['New'];

  // Multi-select plumbing: an empty Set means "All", and the "All" item both
  // shows that state (via the sentinel key) and clears the selection on click.
  const makeMultiHandler = (current, setter) => (keys) => {
    const next = new Set(keys);
    if (next.has('All')) {
      next.delete('All');
      if (next.size > current.size) setter(next); // a real item was toggled while "All" was shown
      else setter(new Set());                     // "All" itself was clicked — clear the selection
    } else {
      setter(next);
    }
  };
  const multiKeys = (set) => (set.size ? set : new Set(['All']));
  const multiLabel = (set, allLabel) =>
    set.size === 0 ? allLabel : set.size === 1 ? [...set][0] : `${set.size} ${t('selected') || 'selected'}`;

  // Export the rows exactly as currently filtered and sorted (all pages) as a
  // real .xlsx — not CSV, which would strip leading zeros off 03xx phone
  // numbers when Excel type-guesses the cells.
  const exportToExcel = () => {
    const rows = table.getSortedRowModel().rows.map(r => r.original);
    const clean = (name) => (name && name.includes('null') ? name.replace(/\s*null\s*/, '').trim() : (name || ''));
    const headers = [
      t('client') || 'Client', t('phone') || 'Phone', t('stage') || 'Stage', 'Services', t('source') || 'Source',
      t('assignee') || 'Assignee', 
      t('orderDate') || 'Order Date', t('creationDate') || 'Created',
    ];
    const formatDateDMY = (val) => {
      if (!val) return '';
      const str = String(val).trim();
      if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(str)) return str;
      const d = new Date(str);
      if (isNaN(d.getTime())) return str;
      const day = String(d.getUTCDate()).padStart(2, '0');
      const month = String(d.getUTCMonth() + 1).padStart(2, '0');
      const year = d.getUTCFullYear();
      return `${day}-${month}-${year}`;
    };
    const aoa = [headers, ...rows.map(l => [
      clean(l.clientName), String(l.phone || ''), l.stage || '', (l.services || []).map(s => typeof s === 'string' ? s : s.name).join(', '), l.source || '', l.assignee?.username || 'Unassigned',
      formatDateDMY(l.order_date), formatDateDMY(l.inquiryDate),
    ])];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    // Reasonable column widths so the sheet opens readable
    ws['!cols'] = [22, 15, 12, 16, 14, 14, 14, 12, 12, 12, 14, 16, 8].map(w => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Prequalifications');
    XLSX.writeFile(wb, `leads_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Page Heading */}
      <div className="mb-2">
        <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
          {isSales ? t('myLeads') : t('allLeads')}
        </h2>
        <p className="text-gray-500 mt-1">{totalLeads || 0} {t('prequalifications').toLowerCase()}
          {isSales && <span className="ml-1 text-xs text-blue-500 font-medium">{t('yourPipeline') || '(your pipeline)'}</span>}
        </p>
      </div>

      {/* Approval Status Filter Tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200 pb-2 overflow-x-auto">
        {[
          { id: 'All', label: t('allPrequalifications') || 'All Prequalifications' },
          { id: 'PENDING_APPROVAL', label: t('pendingApproval') || 'Pending Approval', isPending: true },
          { id: 'APPROVED', label: t('approved') || 'Approved' },
          { id: 'REJECTED', label: t('rejected') || 'Rejected' },
        ].map((tab) => {
          const isActive = approvalFilter === tab.id;
          const pendingCount = (data || []).filter(p => (p.approvalStatus || 'APPROVED') === 'PENDING_APPROVAL').length;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setApprovalFilter(tab.id);
                setPaginationState(prev => ({ ...prev, pageIndex: 0 }));
              }}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 whitespace-nowrap ${
                isActive
                  ? tab.isPending
                    ? 'bg-amber-500 text-white shadow-sm'
                    : tab.id === 'APPROVED'
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : tab.id === 'REJECTED'
                    ? 'bg-rose-600 text-white shadow-sm'
                    : 'bg-blue-600 text-white shadow-sm'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {tab.isPending && (
                <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-white' : 'bg-amber-500'} ${pendingCount > 0 ? 'animate-ping' : ''}`} />
              )}
              <span>{tab.label}</span>
              {tab.isPending && pendingCount > 0 && (
                <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                  isActive ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-800'
                }`}>
                  {pendingCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Warning Message */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 p-3 md:p-4 rounded-xl flex flex-col sm:flex-row items-start sm:items-center gap-3 shadow-sm transition-all hover:shadow-md">
        <div className="bg-white/60 p-2 rounded-lg text-amber-600 shrink-0 shadow-sm border border-amber-100">
          <AlertTriangle className="w-5 h-5" />
        </div>
        <div className="text-sm text-amber-900 flex-1 leading-relaxed">
          <strong className="font-semibold text-amber-700 mr-1.5">{t('warning') || 'Warning'}:</strong>
          <span className="text-amber-800/90">{t('leadLockWarning') || 'Moving a prequalification to the "Won" or "Lost" stage will lock the prequalification and it cannot be edited further.'}</span>
        </div>
      </div>

      {/* Header & Toolbar */}
      <div className="bg-white p-4 md:p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col gap-4">

        {/* Top Row: Search & Actions */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">

          {/* Search */}
          <div className="relative w-full lg:max-w-md flex-shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              className="w-full pl-10 pr-4 h-[42px] bg-slate-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              placeholder={t('searchLeads')}
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto justify-between sm:justify-end">
            {/* View Toggle */}
            <div className="flex items-center h-[42px] bg-slate-100 border border-gray-200 rounded-lg p-1 gap-0.5">
              <button
                onClick={() => handleViewMode('list')}
                title={t('listView') || "List View"}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${viewMode === 'list'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                <List className="w-4 h-4" />
                <span className="hidden sm:inline text-xs">{t('listLabel') || 'List'}</span>
              </button>
              <button
                onClick={() => handleViewMode('kanban')}
                title={t('kanbanBoard') || "Kanban Board"}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${viewMode === 'kanban'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                <LayoutGrid className="w-4 h-4" />
                <span className="hidden sm:inline text-xs">{t('boardLabel') || 'Board'}</span>
              </button>
            </div>

            <button
              onClick={exportToExcel}
              className="flex-1 sm:flex-none justify-center h-[42px] bg-sky-50 text-sky-700 border border-sky-200 px-3 sm:px-4 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-sky-100 transition-colors whitespace-nowrap shadow-sm"
            >
              <Download className="w-4 h-4" /> <span className="hidden sm:inline">{t('exportExcel') || 'Export'}</span>
            </button>
            {!isSales && (
              <button
                onClick={() => setShowImportSetup(true)}
                className="flex-1 sm:flex-none justify-center h-[42px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 sm:px-4 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-emerald-100 transition-colors whitespace-nowrap shadow-sm"
              >
                <Upload className="w-4 h-4" /> <span className="hidden sm:inline">{t('importCsv')}</span>
              </button>
            )}
            {onTruncateLeads && !isSales && (
              <button
                onClick={() => setShowDeleteAllConfirm(true)}
                className="flex-1 sm:flex-none justify-center h-[42px] bg-red-50 text-red-700 border border-red-200 px-3 sm:px-4 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-red-100 transition-colors whitespace-nowrap shadow-sm"
              >
                <Trash2 className="w-4 h-4" /> <span className="hidden sm:inline">{t('deleteAll')}</span>
              </button>
            )}
            <button
              onClick={onShowNewLead}
              className="flex-1 sm:flex-none justify-center h-[42px] bg-blue-600 text-white px-4 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-blue-700 transition-colors whitespace-nowrap shadow-sm"
            >
              <Plus className="w-4 h-4" /> <span>{t('addLead')}</span>
            </button>
          </div>
        </div>

        {/* Bottom Row: Filters */}
        <div className="flex flex-col xl:flex-row items-stretch xl:items-center justify-between gap-3 pt-4 border-t border-gray-100">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 text-gray-400 font-medium text-sm mr-1">
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filters:</span>
            </div>
            <Dropdown className="w-full sm:w-auto">
              <DropdownTrigger>
                <button
                  className="w-full sm:w-auto min-w-[140px] bg-white hover:bg-slate-50 border border-gray-200 rounded-lg text-sm px-3 h-[40px] focus:ring-2 focus:ring-blue-500 outline-none flex justify-between items-center text-left shadow-sm transition-colors"
                >
                  <span className="font-medium text-gray-700">{multiLabel(stageFilter, t('allStages'))}</span>
                  <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                </button>
              </DropdownTrigger>
              <DropdownPopover className="w-[180px] max-h-60 overflow-y-auto">
                <DropdownMenu
                  selectedKeys={multiKeys(stageFilter)}
                  selectionMode="multiple"
                  onSelectionChange={makeMultiHandler(stageFilter, setStageFilter)}
                >
                  <DropdownItem id="All">{t('allStages')}</DropdownItem>
                  {filterStages.map((s) => (
                    <DropdownItem key={s} id={s}>
                      {s}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </DropdownPopover>
            </Dropdown>

            {!isSales && (
              <Dropdown className="w-full sm:w-auto">
                <DropdownTrigger>
                  <button
                    className="w-full sm:w-auto min-w-[140px] bg-white hover:bg-slate-50 border border-gray-200 rounded-lg text-sm px-3 h-[40px] focus:ring-2 focus:ring-blue-500 outline-none flex justify-between items-center text-left shadow-sm transition-colors"
                  >
                    <span className="font-medium text-gray-700">{multiLabel(assigneeFilter, t('allAssignees') || 'All Assignees')}</span>
                    <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                  </button>
                </DropdownTrigger>
                <DropdownPopover className="w-[180px] max-h-60 overflow-y-auto">
                  <DropdownMenu
                    selectedKeys={multiKeys(assigneeFilter)}
                    selectionMode="multiple"
                    onSelectionChange={makeMultiHandler(assigneeFilter, setAssigneeFilter)}
                  >
                    <DropdownItem id="All">{t('allAssignees') || 'All Assignees'}</DropdownItem>
                    <DropdownItem id="Unassigned">{t('unassigned') || 'Unassigned'}</DropdownItem>
                    {uniqueAssignees.map((a) => (
                      <DropdownItem key={a} id={a}>
                        {a}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </DropdownPopover>
              </Dropdown>
            )}

            <Dropdown className="w-full sm:w-auto">
              <DropdownTrigger>
                <button
                  className="w-full sm:w-auto min-w-[140px] bg-white hover:bg-slate-50 border border-gray-200 rounded-lg text-sm px-3 h-[40px] focus:ring-2 focus:ring-blue-500 outline-none flex justify-between items-center text-left shadow-sm transition-colors"
                >
                  <span className="font-medium text-gray-700">{multiLabel(sourceFilter, t('allSources') || 'All Sources')}</span>
                  <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                </button>
              </DropdownTrigger>
              <DropdownPopover className="w-[220px] max-h-60 overflow-y-auto">
                <DropdownMenu
                  selectedKeys={multiKeys(sourceFilter)}
                  selectionMode="multiple"
                  onSelectionChange={makeMultiHandler(sourceFilter, setSourceFilter)}
                >
                  <DropdownItem id="All">{t('allSources') || 'All Sources'}</DropdownItem>
                  {uniqueSources.map((s) => (
                    <DropdownItem key={s} id={s}>
                      {s}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </DropdownPopover>
            </Dropdown>
          </div>

          <div className="flex flex-wrap items-center gap-2 justify-start xl:justify-end">
            {/* Date type selector */}
            <Dropdown className="w-full sm:w-auto">
              <DropdownTrigger>
                <button
                  className="w-full sm:w-auto sm:min-w-[170px] bg-white hover:bg-slate-50 border border-gray-200 rounded-lg text-sm px-3 h-[40px] focus:ring-2 focus:ring-blue-500 outline-none flex justify-between items-center text-left shadow-sm transition-colors"
                >
                  <span className="font-medium text-gray-700 truncate">
                    <span className="text-gray-400 font-normal">{t('filterDatesBy') || 'Filter by'}: </span>
                    {dateFilterType === 'eventDate' && (t('eventDate') || 'Event Date')}
                    {dateFilterType === 'enlistedDate' && (t('enlistedDate') || 'Enlisted Date')}
                    {dateFilterType === 'creationDate' && (t('creationDate') || 'Creation Date')}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                </button>
              </DropdownTrigger>
              <DropdownPopover className="w-[200px] max-h-60 overflow-y-auto">
                <DropdownMenu
                  selectedKeys={new Set([dateFilterType])}
                  selectionMode="single"
                  onSelectionChange={(keys) => setDateFilterType(Array.from(keys)[0])}
                >
                  
                  <DropdownItem id="enlistedDate">{t('enlistedDate') || 'Enlisted Date'}</DropdownItem>
                  <DropdownItem id="creationDate">{t('creationDate') || 'Creation Date'}</DropdownItem>
                </DropdownMenu>
              </DropdownPopover>

            </Dropdown>

            {/* Date range pickers */}
            <div className="flex items-center gap-1.5 w-full sm:w-auto">
              <DatePicker
                className="flex-1 sm:flex-none sm:w-[130px]"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth={false}
              />
              <span className="text-gray-400 font-medium shrink-0">–</span>
              <DatePicker
                className="flex-1 sm:flex-none sm:w-[130px]"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth={false}
              />
            </div>

            {(startDate || endDate) && (
              <button
                onClick={() => { setStartDate(''); setEndDate(''); }}
                className="text-xs font-semibold text-red-500 px-3 h-[40px] hover:bg-red-50 rounded-lg border border-transparent hover:border-red-200 transition-colors flex-shrink-0"
              >
                {t('clear')}
              </button>
            )}
          </div>

        </div>
      </div>

      {/* ──────── Kanban Board View ─────────────────────────────────── */}
      {viewMode === 'kanban' && (
          <PrequalificationsKanbanView
            filteredData={roleFilteredData}
            isLoading={isLoading}
            onSelectLead={onSelectLead}
            onStageChange={onStageChange}
            newPrequalificationIds={newPrequalificationIds}
            viewedPrequalificationIds={viewedPrequalificationIds}
            stages={activeStages}
          />
      )}

      {/* ──────── List View (Mobile + Desktop) ─────────────────────── */}
      {viewMode === 'list' && (
        <div className="space-y-4">
          {/* Mobile Card View */}
          <div className={`md:hidden space-y-3 ${isLoading ? 'opacity-70 pointer-events-none' : ''}`}>
            {isLoading && data.length === 0 ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <Skeleton width={140} height={18} animation="wave" />
                        <Skeleton width={110} height={14} animation="wave" />
                      </div>
                      <Skeleton variant="rounded" width={70} height={22} animation="wave" />
                    </div>
                    <div className="pt-3 border-t flex justify-between">
                      <Skeleton width={90} height={14} animation="wave" />
                      <Skeleton width={70} height={14} animation="wave" />
                    </div>
                  </div>
                ))}
              </div>
            ) : table.getRowModel().rows.length === 0 ? (
              <div className="bg-white p-8 rounded-xl border border-gray-200 text-center text-gray-500">
                {t('noLeadsFound') || 'No prequalifications found.'}
              </div>
            ) : (
              table.getRowModel().rows.map((row) => {
                const stageColor = getStageColor(row.original.stage);
                const isNew = newPrequalificationIds.includes(row.original.id);
                const isViewed = viewedPrequalificationIds.includes(row.original.id);
                const name = row.original.clientName;
                const displayName = name && name.includes('null') ? name.replace(/\s*null\s*/, '').trim() : name;

                return (
                  <div
                    key={row.id}
                    onClick={() => onSelectLead(row.original)}
                    className={`bg-white rounded-xl border border-gray-200/90 shadow-sm hover:shadow-md transition-all active:scale-[0.995] cursor-pointer overflow-hidden ${
                      isNew ? 'border-l-4 border-l-yellow-400 bg-yellow-50/30' : 'border-l-4 border-l-blue-500'
                    } ${isViewed ? 'opacity-75' : ''}`}
                  >
                    <div className="p-4 space-y-3">
                      {/* Top row: Name & Stage */}
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-bold text-gray-900 text-base leading-snug truncate">
                            {row.original.projectName || displayName}
                          </p>
                          {row.original.projectName && (
                            <p className="text-xs text-gray-500 font-medium flex items-center gap-1 truncate mt-0.5">
                              <User className="w-3.5 h-3.5" /> {displayName}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-xs text-gray-600 font-mono flex items-center gap-1">
                              <Phone className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              {row.original.phone}
                            </span>
                            {row.original.source && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                                {typeof row.original.source === 'object' ? row.original.source?.name : row.original.source}
                              </span>
                            )}
                          </div>
                        </div>
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-bold inline-block whitespace-nowrap shadow-sm shrink-0 ${stageColor.bg} ${stageColor.text}`}
                        >
                          {row.original.stage}
                        </span>
                      </div>

                      {/* Middle: Financials & Manager */}
                      <div className="bg-slate-50 border border-slate-100 rounded-lg p-2.5 grid grid-cols-2 gap-2 text-xs">
                        <div className="flex flex-col">
                          <span className="text-gray-400 font-medium">{t('quotationAmount') || 'Quotation'}</span>
                          <span className="text-sm font-bold text-emerald-600 mt-0.5">
                            {formatCurrency(row.original.quotationAmount)}
                          </span>
                        </div>
                        <div className="flex flex-col border-l border-slate-200 pl-2">
                          <span className="text-gray-400 font-medium">{t('clientBudget') || 'Budget'}</span>
                          <span className="text-sm font-bold text-blue-600 mt-0.5">
                            {row.original.clientBudget ? formatCurrency(row.original.clientBudget) : '—'}
                          </span>
                        </div>
                      </div>

                      {/* Bottom footer: Date & Details */}
                      <div className="flex justify-between items-center pt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1.5 text-gray-600 font-medium">
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          {/* Card view has room for one date, so it shows the
                              one currently being sorted on — the reason the
                              rows are in the order they're in. */}
                          {(() => {
                            const active = sorting[0]?.id;
                            
                            if (active === 'orderDate') return <span>{t('orderDate') || 'Order'}: {formatDate(row.original.order_date)}</span>;
                            return <span>{t('creationDate') || 'Created'}: {formatDate(row.original.inquiryDate)}</span>;
                          })()}
                        </span>
                        <div className="flex items-center gap-1 text-blue-600 font-semibold">
                          <span>{t('viewDetails') || 'Details'}</span>
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Desktop Table View */}
          <div className={`hidden md:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden ${isLoading ? 'opacity-70 pointer-events-none' : ''}`}>
            {isLoading && data.length === 0 ? (
              <div className="p-4 space-y-4">
                <div className="flex items-center gap-4 border-b pb-3">
                  {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} width={100} height={16} animation="wave" />)}
                </div>
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-4 py-2 border-b last:border-0">
                    <Skeleton width={150} height={16} animation="wave" />
                    <Skeleton width={100} height={14} animation="wave" />
                    <Skeleton variant="rounded" width={80} height={24} animation="wave" />
                    <Skeleton width={80} height={14} animation="wave" />
                    <Skeleton width={80} height={14} animation="wave" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-gray-200">
                    {table.getHeaderGroups().map(headerGroup => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                          >
                            {header.isPlaceholder ? null : (
                              <div
                                className={header.column.getCanSort() ? 'cursor-pointer select-none flex items-center gap-2' : ''}
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {{
                                  asc: <ChevronUp className="w-4 h-4" />,
                                  desc: <ChevronDown className="w-4 h-4" />,
                                }[header.column.getIsSorted()] ?? null}
                              </div>
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {table.getRowModel().rows.map((row) => {
                      const isNew = newPrequalificationIds.includes(row.original.id);
                      const isViewed = viewedPrequalificationIds.includes(row.original.id);

                      return (
                        <tr
                          key={row.id}
                          className={`
                        transition-colors 
                        ${isNew ? 'bg-yellow-50 hover:bg-yellow-100 border-l-4 border-l-yellow-400' : ''}
                        ${isViewed ? 'opacity-60 hover:opacity-100' : 'hover:bg-slate-50'}
                    `}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id} className="px-6 py-4 whitespace-nowrap text-sm">
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination Controls (Shared for Mobile & Desktop) */}
          {data.length > 0 && (
            <div className="p-4 rounded-xl border border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 bg-white sm:bg-slate-50 shadow-sm">
              {/* Page Size Selector */}
              <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-start">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">{t('show')}</span>
                  <Dropdown>
                    <DropdownTrigger>
                      <button className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex justify-between items-center w-[70px] shadow-sm">
                        {table.getState().pagination.pageSize}
                        <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                      </button>
                    </DropdownTrigger>
                    <DropdownPopover className="w-[70px]" placement="top">
                      <DropdownMenu
                        selectedKeys={new Set([table.getState().pagination.pageSize.toString()])}
                        selectionMode="single"
                        onSelectionChange={(keys) => table.setPageSize(Number(Array.from(keys)[0]))}
                      >
                        {[10, 20, 50].map((pageSize) => (
                          <DropdownItem key={pageSize} id={pageSize.toString()}>
                            {pageSize}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </DropdownPopover>
                  </Dropdown>
                  <span className="text-sm text-gray-600">{t('perPage')}</span>
                </div>

                {/* Mobile page count */}
                <div className="sm:hidden text-xs text-gray-600 font-medium">
                  {t('page')} <span className="font-bold text-gray-900">{table.getState().pagination.pageIndex + 1}</span> / <span className="font-bold text-gray-900">{table.getPageCount()}</span>
                </div>
              </div>

              {/* Desktop Page Info */}
              <div className="hidden sm:block text-sm text-gray-600">
                {t('page')}{' '}
                <span className="font-semibold text-gray-900">
                  {table.getState().pagination.pageIndex + 1}
                </span>{' '}
                {t('of')}{' '}
                <span className="font-semibold text-gray-900">
                  {table.getPageCount()}
                </span>
                {' \u00B7 '}
                <span className="text-gray-500">
                  {roleFilteredData.length} {t('prequalifications').toLowerCase()}
                </span>
              </div>

              {/* Navigation buttons */}
              <div className="flex items-center justify-center gap-1 w-full sm:w-auto">
                <button
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                  title="First page"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                  title="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="hidden sm:flex items-center gap-1 mx-2">
                  {Array.from({ length: Math.min(5, table.getPageCount()) }, (_, i) => {
                    const currentPage = table.getState().pagination.pageIndex;
                    const totalPages = table.getPageCount();
                    let pageNum;

                    if (totalPages <= 5) {
                      pageNum = i;
                    } else if (currentPage <= 2) {
                      pageNum = i;
                    } else if (currentPage >= totalPages - 3) {
                      pageNum = totalPages - 5 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => table.setPageIndex(pageNum)}
                        className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-200 hover:bg-gray-50 text-gray-700'
                          }`}
                      >
                        {pageNum + 1}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                  title="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                  className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                  title="Last page"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}{/* end list view */}

      {/* CSV Import Progress Modal */}
      <CsvImportModal progress={importProgress} onClose={closeImportModal} />

      {/* CSV Setup Modal */}
      <CsvSetupModal
        isOpen={showImportSetup}
        onClose={() => setShowImportSetup(false)}
        onStartImport={(file) => {
          setShowImportSetup(false);
          onFileUpload({ target: { files: [file], value: '' } });
        }}
      />

      {/* Delete All Confirm Modal */}
      {showDeleteAllConfirm && (
        <DeleteConfirmModal
          title={t('deleteAll')}
          message="Are you sure you want to delete all prequalifications? This action cannot be undone and will remove all data permanently."
          onConfirm={() => {
            onTruncateLeads();
            setShowDeleteAllConfirm(false);
          }}
          onCancel={() => setShowDeleteAllConfirm(false)}
        />
      )}
    </div>
  );
}
